import React from 'react';
import { logFoodAPI } from '../../utils/api';
import { Search, ArrowLeft, Plus, Minus, Check, X, Loader2 } from 'lucide-react';
import { cleanLabel } from './labels';
import useKeyboardInset from '../../hooks/useKeyboardInset';

// FatSecret-backed search + serving picker (bottom sheet) for the AI-log review flow. Reuses the SAME
// canonical normal Log Food endpoints (/log-food/search/foods + /log-food/food/{external_id}) as the
// regular food flow — NOT the legacy preview-only /ai-log/* endpoints (which are gated behind the old
// AI-log flags/allowlist and fail in staging). Selecting a result hydrates full FatSecret details
// (every real serving, with serving_id/metric metadata) so the exact locked FSID + trusted serving
// options reach Review. No DB writes here; the rebuild backend stays authoritative for nutrition.
const _fsid = (externalId) => {
  const s = String(externalId == null ? '' : externalId);
  const tail = s.split('_').pop();
  return tail || s || null;
};

export default function FoodSearchSheet({ title, presetQuery, prefer, onAdd, onClose }) {
  const [q, setQ] = React.useState(presetQuery || '');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [error, setError] = React.useState('');
  const [sel, setSel] = React.useState(null);       // { record, component, servings, ids… }
  const [loadingSel, setLoadingSel] = React.useState(false);
  const [amount, setAmount] = React.useState(1);
  const [unit, setUnit] = React.useState(null);
  const r1 = (n) => Math.round(n * 10) / 10;
  // Lift this fixed bottom sheet above the iOS keyboard (see useKeyboardInset). With the keyboard
  // open the home-indicator safe area is covered, so only the normal padding remains.
  const kb = useKeyboardInset();

  const runSearch = async () => {
    const term = q.trim();
    if (term.length < 2) { setError('Type at least 2 letters.'); return; }
    setLoading(true); setError(''); setSearched(true); setResults([]);
    try {
      const r = await logFoodAPI.searchFoods({ q: term, limit: 20 });
      setResults((r.data && r.data.items) || []);
    } catch (e) { setError('Search failed — please try again.'); }
    setLoading(false);
  };

  // PHASE 3: when opened prefilled with a concept (e.g. a hidden-ingredient "milk"/"butter" prompt),
  // run the search immediately so the user lands on results for that concept.
  React.useEffect(() => {
    if (presetQuery && presetQuery.trim().length >= 2) runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map a canonical FatSecret serving_option to the picker's serving shape. Preserves the real
  // serving_id + measurement metadata so an explicit selection targets the EXACT FatSecret serving.
  const _opt = (o) => ({
    unit: o.unit,
    size: (o.size != null ? o.size : 1),
    perUnit: o.macros || { calories: 0, protein: 0, carbs: 0, fat: 0 },
    serving_id: o.serving_id != null ? o.serving_id : null,
    measurement_description: o.measurement_description != null ? o.measurement_description : null,
  });

  const pick = async (f) => {
    setLoadingSel(true); setError('');
    try {
      const r = await logFoodAPI.getFoodDetails(f.external_id);
      const item = (r.data && r.data.item) || null;
      const opts = ((item && item.serving_options) || []).map(_opt);
      const servings = opts.length ? opts
        : [{ unit: (item && item.serving && item.serving.unit) || 'serving',
             size: (item && item.serving && item.serving.size) || 1,
             perUnit: (item && item.macros) || { calories: 0, protein: 0, carbs: 0, fat: 0 },
             serving_id: null, measurement_description: null }];
      if (!item) { setError('No serving info for that item.'); setLoadingSel(false); return; }
      setSel({ record: item.name, component: 'other', servings,
        fatsecret_food_id: _fsid(item.external_id), external_id: item.external_id,
        match_source: 'fatsecret_search',
        brand: item.brand != null ? item.brand : null,
        category: item.category != null ? item.category : null });
      setUnit(servings[0].unit); setAmount(servings[0].size);
    } catch (e) { setError('Could not load that item.'); }
    setLoadingSel(false);
  };

  const per = sel ? (sel.servings.find((o) => o.unit === unit) || sel.servings[0]).perUnit : null;
  const cals = per ? Math.round((per.calories || 0) * amount) : 0;

  const add = () => {
    const opt = sel.servings.find((o) => o.unit === unit) || sel.servings[0];
    // preserve the selected food's EXACT identity so the row/commit never mixes a new record with an
    // old FatSecret id. The chosen candidate carries the same identity for later candidate swaps.
    const cand = { record: sel.record, unit, amount, perUnit: opt.perUnit,
      fatsecret_food_id: sel.fatsecret_food_id, external_id: sel.external_id,
      match_source: sel.match_source, brand: sel.brand, category: sel.category, component: sel.component };
    onAdd({
      record: sel.record, unit, amount, perUnit: opt.perUnit,
      component: (sel.component && sel.component !== 'other') ? sel.component : (prefer || null),
      fatsecret_food_id: sel.fatsecret_food_id, external_id: sel.external_id,
      match_source: sel.match_source, brand: sel.brand, category: sel.category,
      // Trusted provider serving options (with real serving_id) so a rebuild provider swap keeps them.
      serving_options: sel.servings.map((o) => ({ unit: o.unit, size: o.size, perUnit: o.perUnit,
        serving_id: o.serving_id, measurement_description: o.measurement_description })),
      candidates: [cand],
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end" style={kb ? { bottom: kb } : undefined} data-testid="food-search-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="search-backdrop" />
      <div className="relative w-full bg-background rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto shadow-xl" data-testid="search-panel"
        style={kb
          ? { maxHeight: 'calc(100% - env(safe-area-inset-top) - 0.5rem)', paddingBottom: '1rem' }
          : { paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        <div className="w-10 h-1 bg-border rounded-full mx-auto mb-3" />
        {!sel ? (
          <>
            {/* Title + input + Search stay pinned while results scroll under them. */}
            <div className="sticky top-0 z-10 -mx-4 px-4 pb-1 bg-background" data-testid="search-controls">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-foreground">{title || 'Add food'}</p>
              <button onClick={onClose} className="text-muted-foreground" data-testid="search-close"><X size={18} /></button>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-card border border-border/40 rounded-xl px-3 h-11">
                <Search size={16} className="text-muted-foreground" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  placeholder="Search foods, then press Search"
                  className="flex-1 bg-transparent text-sm outline-none" data-testid="search-input" />
              </div>
              <button onClick={runSearch} disabled={loading} className="h-11 px-4 rounded-xl bg-accent text-white text-sm font-semibold disabled:opacity-60" data-testid="search-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : 'Search'}
              </button>
            </div>
            </div>
            {error && <p className="text-xs text-destructive mt-2" data-testid="search-error">{error}</p>}
            <div className="mt-3 space-y-1.5">
              {loading && <div className="flex items-center justify-center py-6 text-muted-foreground"><Loader2 size={18} className="animate-spin" /></div>}
              {!loading && results.map((f) => (
                <button key={f.external_id} onClick={() => pick(f)} disabled={loadingSel} className="w-full flex items-center justify-between gap-2 bg-card border border-border/40 rounded-xl px-3 py-2.5 hover:border-accent text-left disabled:opacity-60" data-testid={'search-result-' + f.external_id}>
                  <span className="min-w-0">
                    <span className="text-sm text-foreground truncate block">{cleanLabel(f.name)}{f.brand ? <span className="text-muted-foreground"> · {f.brand}</span> : null}</span>
                    <span className="text-[11px] text-muted-foreground">{f.category === 'BRANDED' ? 'Packaged' : 'Whole food'}{f.macros && f.macros.calories != null ? ` · ${Math.round(f.macros.calories)} cal${f.serving && f.serving.description ? ' / ' + cleanLabel(f.serving.description) : ''}` : ''}</span>
                  </span>
                  <Plus size={16} className="text-accent shrink-0" />
                </button>
              ))}
              {!loading && searched && results.length === 0 && !error && (
                <p className="text-xs text-muted-foreground py-4 text-center" data-testid="search-empty">No matches — try another word.</p>
              )}
              {!loading && !searched && (
                <p className="text-xs text-muted-foreground py-4 text-center">Type a food and press Search.</p>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <button onClick={() => setSel(null)} className="text-muted-foreground" data-testid="search-back"><ArrowLeft size={18} /></button>
              <p className="text-sm font-semibold text-foreground">{cleanLabel(sel.record)}</p>
              <span className="ml-auto text-sm font-bold text-foreground" data-testid="search-portion-cals">{cals} cal</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Amount</span>
              <div className="flex items-center gap-4">
                <button onClick={() => setAmount(Math.max(0.25, r1(amount - 0.25)))} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="search-amt-minus"><Minus size={16} /></button>
                <input type="number" inputMode="decimal" step="0.25" min="0.25" value={amount}
                  onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setAmount(r1(v)); }}
                  onFocus={(e) => e.target.select()}
                  className="font-mono text-lg font-bold text-foreground w-16 text-center bg-card border border-border/40 rounded-lg py-1 outline-none focus:border-accent" data-testid="search-amt-input" />
                <button onClick={() => setAmount(r1(amount + 0.25))} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="search-amt-plus"><Plus size={16} /></button>
              </div>
            </div>
            {sel.servings.length > 1 && (
              <div className="mt-4 space-y-1.5">
                <p className="text-xs text-muted-foreground">Serving</p>
                {sel.servings.map((o) => (
                  <button key={o.unit} onClick={() => { setUnit(o.unit); setAmount(o.size); }} className={'w-full flex items-center justify-between rounded-xl px-3 py-2.5 border ' + (o.unit === unit ? 'border-accent bg-accent/5' : 'border-border/40 bg-card')} data-testid={'search-unit-' + o.unit.replace(/[^a-z0-9]/gi, '')}>
                    <span className="text-sm text-foreground">{cleanLabel(o.unit)}</span>
                    {o.unit === unit && <Check size={15} className="text-accent" />}
                  </button>
                ))}
              </div>
            )}
            <button onClick={add} className="w-full mt-5 h-12 rounded-xl bg-accent text-white font-semibold" data-testid="search-add">Add to meal · {cals} cal</button>
          </>
        )}
      </div>
    </div>
  );
}
